import React, { PureComponent } from 'react';
import Text from 'components/core/Text';
import imageUrlGenerator from '../../../../../utils/imageUrlGenerator';
import groupProgramsByCategory from '../../../../../utils/groupProgramsByCategory';
import { ProgramCategory } from 'reducers/programs/types';
import Container from '../../Container';
import CurriculumIntro, { SUPPLEMENTAL_ANCHOR_ID } from '../CurriculumIntro';
import LinkIcon from '../../../../LinkIcon';
import LockIcon from '../../../../LockIcon';
import { Wrapper, Section } from '../styles';
import {
  Programs,
  Program,
  Image,
  Description,
  CategorySection,
  CategoryHeader,
  CategoryNumber,
  CategoryTitleRow,
  CategorySubtitle,
  CategoryCount,
  ScrollRail,
} from './styles';

const getLinkUrl = (program = {}) =>
  program.isExternal ? program.externalLink : `/programs/${program.id}`;

const getLockedLinkUrl = (item) =>
  item.product?.link || `/products/buy?productId=${item.product?.id}`;

const findFoundationProgram = (programs = []) =>
  programs.find((program) => program.category === ProgramCategory.Foundation);

const getSlicedProgramName = (name) => {
  if (name) {
    if (name.length > 21) {
      return `${name.slice(0, 21)}...`;
    }
    return name;
  }
  return '';
};

const TRANSPARENT_PIXEL =
  'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==';

const handleImageError = (e) => {
  e.currentTarget.onerror = null;
  e.currentTarget.src = TRANSPARENT_PIXEL;
};

const renderProgramCard = (item) => {
  const {
    completedSessionsCount,
    sessionsCount,
    picture,
    name,
    isExternal,
    nextTrackSessionId,
    programTrack,
    id,
  } = item;
  const programTrackSessions = programTrack?.programTrackSessions;

  const ptsIdx = programTrackSessions?.findIndex((pts) => pts.id === nextTrackSessionId);
  const isLooping = programTrack?.loopBackToTrackSessionId != null;
  const sessionDaysCompleted = (ptsIdx / (sessionsCount - 1)) * 100;
  const image = imageUrlGenerator(picture, '290x163', '357x185');

  return (
    <Program
      key={`owned-${id}`}
      sessionDaysCompleted={sessionDaysCompleted}
      isLooping={isLooping}
      href={getLinkUrl(item)}
    >
      {isExternal && <LinkIcon isSmall />}
      <Image src={image} alt="" onError={handleImageError} />
      <Description>
        <Text variant="mainHeader" isCondensed color="white" tag="h4" mb={1} withShadow>
          {getSlicedProgramName(name)}
        </Text>
        {!isExternal && (
          <Text variant="caption" color="white" withShadow>
            {completedSessionsCount} {completedSessionsCount === 1 ? 'SESSION' : 'SESSIONS'}{' '}
            COMPLETED
          </Text>
        )}
      </Description>
    </Program>
  );
};

const renderLockedCard = (item) => {
  const { picture, name, id } = item;
  const image = imageUrlGenerator(picture, '290x163', '357x185');

  return (
    <Program key={`locked-${id}`} locked href={getLockedLinkUrl(item)}>
      <LockIcon isSmall />
      <Image src={image} alt="" onError={handleImageError} />
      <Description>
        <Text variant="mainHeader" isCondensed color="white" tag="h4" mb={1} withShadow>
          {getSlicedProgramName(name)}
        </Text>
        <Text variant="caption" color="white" withShadow>
          LEARN MORE
        </Text>
      </Description>
    </Program>
  );
};

class Purchased extends PureComponent {
  render() {
    const { userPrograms, availablePrograms } = this.props;

    const { all: owned = [] } = userPrograms || {};
    const { all: locked = [] } = availablePrograms || {};
    const categorySections = groupProgramsByCategory(owned, locked);
    const hasLibrary = categorySections.length > 0;

    const ownedFoundation = findFoundationProgram(owned);
    const lockedFoundation = findFoundationProgram(locked);
    let elementsHref = null;
    if (ownedFoundation) {
      elementsHref = getLinkUrl(ownedFoundation);
    } else if (lockedFoundation) {
      elementsHref = getLockedLinkUrl(lockedFoundation);
    }

    return (
      <Wrapper>
        <Section>
          <Container>
            <CurriculumIntro hasLibrary={hasLibrary} elementsHref={elementsHref} />
            {hasLibrary && (
              <Text variant="sectionHeader" transform="uppercase" mb={5}>
                Your Program Library
              </Text>
            )}
            {categorySections.map(
              (
                { category, title, subtitle, owned: ownedInSection, locked: lockedInSection },
                idx,
              ) => {
                const count = ownedInSection.length + lockedInSection.length;
                return (
                  <CategorySection
                    key={category}
                    id={
                      category === ProgramCategory.Supplemental ? SUPPLEMENTAL_ANCHOR_ID : undefined
                    }
                  >
                    <CategoryHeader>
                      <CategoryNumber>{String(idx + 1).padStart(2, '0')}</CategoryNumber>
                      <CategoryTitleRow>
                        <Text variant="displayM" transform="uppercase" isCondensed tag="h3">
                          {title}
                        </Text>
                      </CategoryTitleRow>
                      <CategoryCount>
                        {count} {count === 1 ? 'Program' : 'Programs'}
                      </CategoryCount>
                      <CategorySubtitle>
                        <Text variant="textSmall" color="gray">
                          {subtitle}
                        </Text>
                      </CategorySubtitle>
                    </CategoryHeader>
                    <ScrollRail>
                      <Programs>
                        {ownedInSection.map(renderProgramCard)}
                        {lockedInSection.map(renderLockedCard)}
                      </Programs>
                    </ScrollRail>
                  </CategorySection>
                );
              },
            )}
          </Container>
        </Section>
      </Wrapper>
    );
  }
}

export default Purchased;
